// frontend-repo/src/pages/Chat.js
import { useEffect, useMemo, useRef, useState } from "react";
import ChatSidebar from "../components/ChatSidebar";
import MessageInput from "../components/MessageInput";
import MessageList from "../components/MessageList";
import WelcomeScreen from "../components/WelcomeScreen";
import { UserAuth } from "../context/AuthContext";
import { useBackendContext } from "../hooks/useBackendContext";
import { createNewConversation, updateConversationTitle } from "../utils/conversationHelpers";
import { buildContextAwarePrompt } from "../utils/promptBuilder";

import { callAzureOpenAI } from "../utils/azureOpenAI";

import "../css/chat/chat.css";

// Main LLM Component with Backend Context Integration
const Chat = () => {
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Backend context integration
  const { backendContext, processContext, error: contextError, getContextForQuery } = useBackendContext();
  const { user } = UserAuth();

  const currentConversation = conversations.find(c => c.id === currentConversationId);
  // Memoize messages to prevent useEffect dependency changes on every render
  const messages = useMemo(
    () => currentConversation?.messages || [],
    [currentConversation?.messages]
  );

  // Determine if we should show welcome screen
  const showWelcome = !currentConversationId || (currentConversation && messages.length === 0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Enhanced context logging
  useEffect(() => {
    if (backendContext) {
      console.log("🔧 Backend context loaded successfully:", {
        totalFiles: backendContext.metadata?.totalFiles,
        pythonFiles: backendContext.metadata?.pythonFiles,
        javascriptFiles: backendContext.metadata?.javascriptFiles,
        apiEndpoints: backendContext.apiEndpoints?.length || 0,
        pythonModules: backendContext.pythonModules?.length || 0,
        lastUpdate: backendContext.metadata?.generatedAt,
        gitCommit: backendContext.metadata?.gitCommit?.substring(0, 8),
      });
    }
  }, [backendContext]);

  // Log process context when loaded
  useEffect(() => {
    if (processContext) {
      console.log("🎯 Process context loaded:", {
        totalProcesses: processContext.totalProcesses,
        models: processContext.models?.length || 0,
        mutations: processContext.mutations?.length || 0,
      });
    }
  }, [processContext]);

  useEffect(() => {
    if (contextError) {
      console.error("❌ Backend context loading failed:", contextError);
    }
  }, [contextError]);

  // Load conversations from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("monikaCodeAssistantConversations");
    if (saved) {
      const parsed = JSON.parse(saved);
      setConversations(parsed);
      // Only set current conversation if there are actual conversations with messages
      if (parsed.length > 0 && parsed[0].messages.length > 0) {
        setCurrentConversationId(parsed[0].id);
      }
    }
  }, []);

  // Save conversations to localStorage with quota management
  useEffect(() => {
    if (conversations.length > 0) {
      const STORAGE_KEY = "monikaCodeAssistantConversations";
      const MAX_CONVERSATIONS = 50;

      // Limit conversations to prevent quota issues
      let conversationsToSave = conversations.slice(0, MAX_CONVERSATIONS);

      const trySave = (data) => {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
          return true;
        } catch (e) {
          if (e.name === 'QuotaExceededError') {
            return false;
          }
          console.error('localStorage error:', e);
          return false;
        }
      };

      // Try to save, if quota exceeded, progressively remove old conversations
      if (!trySave(conversationsToSave)) {
        console.warn('localStorage quota exceeded, removing old conversations...');
        while (conversationsToSave.length > 1 && !trySave(conversationsToSave)) {
          conversationsToSave = conversationsToSave.slice(0, -1);
        }
        if (conversationsToSave.length === 1 && !trySave(conversationsToSave)) {
          // Last resort: clear and save only current conversation
          localStorage.removeItem(STORAGE_KEY);
          trySave(conversationsToSave);
        }
      }
    }
  }, [conversations]);

  const handleSuggestionClick = suggestion => {
    setInput(suggestion);
    // If no conversation exists, create one when suggestion is clicked
    if (!currentConversationId) {
      const newConversation = createNewConversation();
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversationId(newConversation.id);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    // Create new conversation if none exists or no current conversation
    let conversationId = currentConversationId;
    if (!conversationId) {
      const newConversation = createNewConversation();
      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversationId(newConversation.id);
      conversationId = newConversation.id;
    }

    const userMessage = { role: "user", content: input.trim() };
    setInput("");

    const updatedMessages = [...messages, userMessage];
    setConversations(prev =>
      prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, messages: updatedMessages, lastMessage: new Date().toLocaleString() }
          : conv
      )
    );

    setIsLoading(true);

    try {
      // 🎯 SMART CONTEXT SELECTION - This is the key change!
      const querySpecificContext = getContextForQuery ? getContextForQuery(userMessage.content) : backendContext;

      console.log("🔍 Context selection:", {
        originalQuery: userMessage.content,
        contextType: querySpecificContext?.isSummary
          ? "summary"
          : querySpecificContext?.querySpecific
          ? "query-specific"
          : "full",
        contextSize: querySpecificContext
          ? `${Math.round(JSON.stringify(querySpecificContext).length / 1024)}KB`
          : "0KB",
        filesIncluded: Object.keys(querySpecificContext?.files || {}).length,
      });

      // Build context-aware prompt with selected context and live process data
      const enhancedPrompt = buildContextAwarePrompt(userMessage.content, updatedMessages, querySpecificContext, processContext);

      const response = await callAzureOpenAI(enhancedPrompt);
      const assistantMessage = { role: "assistant", content: response };

      const finalMessages = [...updatedMessages, assistantMessage];
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, messages: finalMessages, lastMessage: new Date().toLocaleString() }
            : conv
        )
      );

      updateConversationTitle(conversations, setConversations, conversationId, finalMessages);
    } catch (error) {
      console.error("❌ LLM call failed:", error);
      const errorMessage = {
        role: "assistant",
        content: `Sorry, I encountered an error: ${error.message}`,
      };
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId ? { ...conv, messages: [...updatedMessages, errorMessage] } : conv
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Manual new conversation handler
  const handleNewConversation = () => {
    // Clear current conversation to show welcome screen
    setCurrentConversationId(null);
  };

  return (
    <div className="chat-app">
      <div className="chat-layout">
        <ChatSidebar
          conversations={conversations}
          currentConversationId={currentConversationId}
          onSelectConversation={setCurrentConversationId}
          onNewConversation={handleNewConversation}
          backendContext={backendContext}
          processContext={processContext}
        />

        <div className="chat-main">
          {showWelcome ? (
            <WelcomeScreen onSuggestionClick={handleSuggestionClick} backendContext={backendContext} processContext={processContext} user={user} />
          ) : (
            <div className="chat-messages-area">
              <MessageList messages={messages} isLoading={isLoading} backendContext={backendContext} />
            </div>
          )}

          <div className="chat-input-area">
            <MessageInput
              input={input}
              setInput={setInput}
              isLoading={isLoading}
              onSubmit={handleSubmit}
              backendContext={backendContext}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
